import type { BinaryImage, Component } from "./types";

export interface ConnectedComponentsOptions {
  /** 4- or 8-connectivity for grouping foreground pixels. Defaults to 8. */
  connectivity?: 4 | 8;
}

export interface ConnectedComponentsResult {
  /** Per-pixel label map (row-major). 0 = background. */
  labels: Int32Array;
  /** One entry per connected component, in ascending label order. */
  components: Component[];
}

/**
 * Label connected foreground regions of a binary image using an iterative
 * flood fill (breadth-first) so it never overflows the stack on large blobs.
 */
export function connectedComponents(
  image: BinaryImage,
  { connectivity = 8 }: ConnectedComponentsOptions = {},
): ConnectedComponentsResult {
  const { width, height, data } = image;
  const labels = new Int32Array(width * height);
  const components: Component[] = [];

  const neighbours =
    connectivity === 4
      ? [
          [-1, 0],
          [1, 0],
          [0, -1],
          [0, 1],
        ]
      : [
          [-1, -1],
          [0, -1],
          [1, -1],
          [-1, 0],
          [1, 0],
          [-1, 1],
          [0, 1],
          [1, 1],
        ];

  let nextLabel = 0;
  // Reusable queue of pixel indices for the flood fill.
  const queue = new Int32Array(width * height);

  for (let start = 0; start < data.length; start++) {
    if (data[start] === 0 || labels[start] !== 0) continue;

    nextLabel++;
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    labels[start] = nextLabel;

    let area = 0;
    let sumX = 0;
    let sumY = 0;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    while (head < tail) {
      const idx = queue[head++];
      const x = idx % width;
      const y = (idx / width) | 0;

      area++;
      sumX += x;
      sumY += y;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;

      for (const [dx, dy] of neighbours) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        const nIdx = ny * width + nx;
        if (data[nIdx] === 1 && labels[nIdx] === 0) {
          labels[nIdx] = nextLabel;
          queue[tail++] = nIdx;
        }
      }
    }

    components.push({
      label: nextLabel,
      area,
      bbox: {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
      },
      centroid: { x: sumX / area, y: sumY / area },
    });
  }

  return { labels, components };
}
